import clsx from 'clsx'
import { AppToastContainer, showDeleteToast, showErrorToast } from 'components/utility/toast'
import React, { useEffect, useState } from 'react'
import { selectFlagsForTarget, selectFlagsForRequirementScope, useFlagStore } from 'stores/flagStore'
import { selectAllDocumentsOfRequirement, usePackageStore } from 'stores/packageStore'
import { useShallow } from 'zustand/react/shallow'
import { AddFlagForm } from './AddFlagForm'
import {
  FLAG_STATE,
  APPLICANT_REVIEWING_STATES,
  APPLICANT_COMPLETED_STATES,
  PROCESSOR_REVIEWING_STATES,
  PROCESSOR_RESOLVED_STATES,
  APPLICANT_ROLES,
  PROCESSOR_ROLES,
  BOARD_MEMBER_ROLES,
} from './constants'
import { ApplicantFlagItem, ProcessorFlagItem } from './FlagItem'
import { useAnimatingFlags } from './hooks/useAnimatingFlags'
import { useFlagActions } from './hooks/useFlagActions'

// ─── Tab Definitions ────────────────────────────────────────────────────────

const APPLICANT_TABS = [
  { id: 'reviewing', label: 'reviewing' },
  { id: 'completed', label: 'completed' },
]

const PROCESSOR_TABS = [
  { id: 'reviewing', label: 'reviewing' },
  { id: 'resolved', label: 'resolved' },
]

const isFlagReadonly = ({ role, processorWorking, flag, currentRoundNumber }) => {
  if (APPLICANT_ROLES.includes(role)) {
    return processorWorking || flag.state === FLAG_STATE.ACCEPTED
  } else if (PROCESSOR_ROLES.includes(role)) {
    return (
      !processorWorking ||
      flag.state === FLAG_STATE.REPORTED ||
      (flag.resolved_round_number && flag.resolved_round_number !== currentRoundNumber)
    )
  } else if (BOARD_MEMBER_ROLES.includes(role)) {
    // TODO
  }
}

// ─── Primitives ──────────────────────────────────────────────────────────────

const Header = ({ title, subtitle }) => (
  <div style={{ overflowWrap: 'break-word' }} className="dome-d-flex dome-flex-column">
    <span className="dome-h3 dome-color-darkest-grey">{title}</span>
    <span className="dome-p2 dome-color-med-grey">{subtitle}</span>
  </div>
)

const TabBar = ({ tabs, activeTab, onChange }) => (
  <div className="flag-tabs">
    {tabs.map(({ id, label }) => (
      <button
        key={id}
        type="button"
        className={clsx('flag-tab', activeTab === id && 'flag-tab--active')}
        onClick={() => onChange(id)}
      >
        {label}
      </button>
    ))}
  </div>
)

const RedactReminder = () => (
  <div className="flag-control-area__redact">
    <i className="fa-solid fa-highlighter dome-color-dark-grey" />
    <span className="dome-p2 dome-text-w500 dome-color-dark-grey">did you redact?</span>
    <a href='https://boardpackager.freshdesk.com/support/solutions/articles/43000398374' target='_blank' className="dome-d-flex dome-align-center dome-gap3">
      <span className="dome-p2 dome-text-w500 dome-color-aqua">learn how</span>
      <i className="fa-light fa-arrow-right-long fa-xs dome-color-aqua" />
    </a>
  </div>
)

const EmptyState = () => <span className="dome-p2 dome-color-lite-grey">no notes added</span>

const Root = ({ children, className, onClose }) => (
  <div className={clsx('flag-control-area', className)}>
    <div className="side-flag-icon">
      <i className="fas fa-flag-swallowtail dome-color-white" style={{ fontSize: '11px' }} />
    </div>
    {onClose && (
      <button onClick={onClose} className="flag-control-area__close" type="button">
        <i className="fa-regular fa-xmark dome-color-med-grey" />
      </button>
    )}
    {children}
  </div>
)

export const ControlArea = {
  Root,
  Header,
  TabBar,
  Form: AddFlagForm,
  RedactReminder,
}

// ─── Applicant ────────────────────────────────────────────────────────────────

export const ApplicantControlArea = ({
  role,
  target,
  title,
  subtitle,
  processorWorking,
  fileName,
  className,
  onClose,
  scopeRequirementId,
}) => {
  const scopeDocuments = usePackageStore(useShallow(selectAllDocumentsOfRequirement(scopeRequirementId)))
  const scopeDocumentIds = scopeDocuments?.map((d) => d.id) ?? []
  const docLabelById = Object.fromEntries((scopeDocuments ?? []).map((d) => [d.id, d.label]))
  const flags = useFlagStore(
    useShallow(
      scopeRequirementId
        ? selectFlagsForRequirementScope(scopeRequirementId, scopeDocumentIds)
        : selectFlagsForTarget(target.flaggableType, target.flaggableId)
    )
  )
  const { markFixed, markUnfixed } = useFlagActions(target.packageId)

  const [activeTab, setActiveTab] = useState('reviewing')
  const { animatingFlags, pinFlag, unpinFlagAfterAnimation } = useAnimatingFlags()

  const activeStates = activeTab === 'reviewing' ? APPLICANT_REVIEWING_STATES : APPLICANT_COMPLETED_STATES
  const flagsDisplayed = flags.filter(
    (f) => activeStates.includes(f.state) || animatingFlags.get(f.id)?.activeTab === activeTab
  )

  const handleComplete = async (flag, value) => {
    if (animatingFlags.has(flag.id)) return
    pinFlag(flag, activeTab)
    try {
      await (value ? markFixed : markUnfixed)(flag.id)
    } catch (error) {
      console.error('handleComplete error', error)
      showErrorToast({ message: 'Something went wrong' })
    }
    unpinFlagAfterAnimation(flag)
  }

  return (
    <ControlArea.Root onClose={onClose} className={className}>
      <div className="flag-control-area__scroll-body dome-gap18">
        <div style={{ padding: '0px 18px' }} className="dome-d-flex dome-flex-column dome-gap18">
          <ControlArea.Header title={title} subtitle={subtitle} />
          <span className="dome-p2 dome-color-dark-grey">Click checkbox to mark a flag as complete:</span>
          <ControlArea.TabBar tabs={APPLICANT_TABS} activeTab={activeTab} onChange={setActiveTab} />
        </div>
        <div className="dome-d-flex dome-flex-column" style={{ flex: 1, minHeight: 0 }}>
          <ul
            style={{
              padding: '0px 18px',
              margin: 0,
              listStyle: 'none',
              overflow: 'auto',
              flex: 1,
              minHeight: 0,
            }}
          >
            {flagsDisplayed.map((flag) => {
              const animating = animatingFlags.get(flag.id)
              const displayFlag = animating ? animating.flag : flag
              const isCompleted = animating
                ? !APPLICANT_COMPLETED_STATES.includes(animating.flag.state)
                : APPLICANT_COMPLETED_STATES.includes(flag.state)
              const isReadOnly = isFlagReadonly({ role, processorWorking, flag })

              const flagFileName =
                flag.flaggable_type === 'PackageDocument' ? docLabelById[flag.flaggable_id] ?? fileName : fileName

              return (
                <li key={flag.id}>
                  <ApplicantFlagItem
                    gap={12}
                    flag={displayFlag}
                    fileName={flagFileName}
                    readOnly={isReadOnly}
                    completed={isCompleted}
                    isCompleting={!!animating}
                    onComplete={(value) => handleComplete(flag, value)}
                  />
                </li>
              )
            })}
          </ul>
          <div style={{ padding: '0px 18px' }}>
            {flagsDisplayed.length === 0 && <EmptyState />}
            <ControlArea.RedactReminder />
          </div>
        </div>
      </div>
      <AppToastContainer />
    </ControlArea.Root>
  )
}

// ─── Processor ────────────────────────────────────────────────────────────────

export const ProcessorControlArea = ({
  role,
  target,
  title,
  subtitle,
  currentRoundNumber,
  processorWorking,
  fileName,
  className,
  onClose,
  scopeRequirementId,
}) => {
  const scopeDocuments = usePackageStore(useShallow(selectAllDocumentsOfRequirement(scopeRequirementId)))
  const scopeDocumentIds = scopeDocuments?.map((d) => d.id) ?? []
  const docLabelById = Object.fromEntries((scopeDocuments ?? []).map((d) => [d.id, d.label]))
  const flags = useFlagStore(
    useShallow(
      scopeRequirementId
        ? selectFlagsForRequirementScope(scopeRequirementId, scopeDocumentIds)
        : selectFlagsForTarget(target.flaggableType, target.flaggableId)
    )
  )
  const { create, update, remove, unRemove, accept, reject } = useFlagActions(target.packageId)

  const [activeTab, setActiveTab] = useState('reviewing')
  const [editingFlag, setEditingFlag] = useState(null)
  const { animatingFlags, setAnimatingFlags, pinFlag, unpinFlagAfterAnimation } = useAnimatingFlags()

  // When switching tabs, the resolved flag appears immediately in the resolved tab without waiting for the animation to finish.
  // If the user stays on the current tab, the flag remains visible until the animation completes.
  useEffect(() => {
    setAnimatingFlags(new Map())
  }, [activeTab])

  const activeStates = activeTab === 'reviewing' ? PROCESSOR_REVIEWING_STATES : PROCESSOR_RESOLVED_STATES
  const flagsDisplayed = flags.filter(
    (f) =>
      (activeStates.includes(f.state) || animatingFlags.get(f.id)?.activeTab === activeTab) && f.id !== editingFlag?.id
  )

  const handleUndelete = async (flag) => {
    try {
      await unRemove(flag.id)
    } catch (error) {
      console.error('handleUndelete error', error)
      showErrorToast({ message: 'Something went wrong' })
    }
  }

  const handleDelete = async (flag) => {
    try {
      await remove(flag.id)
      showDeleteToast({ message: 'deleted', onUndo: () => handleUndelete(flag) })
    } catch (error) {
      console.error('handleDelete error', error)
      showErrorToast({ message: 'Something went wrong' })
    }
  }

  const handleSave = async (html, flagId) => {
    try {
      if (flagId) {
        await update(flagId, html)
        setEditingFlag(null)
      } else {
        await create(target.flaggableType, target.flaggableId, html)
      }
    } catch (error) {
      console.error('handleSave error', error)
      const serverError = error?.response?.data?.error
      const message = Array.isArray(serverError) ? serverError.join(', ') : serverError || 'Something went wrong'
      showErrorToast({ message })
    }
  }

  const handleResolve = async (flag, value) => {
    if (animatingFlags.has(flag.id)) return
    pinFlag(flag, activeTab)
    try {
      await (value ? accept : reject)(flag.id)
    } catch (error) {
      console.error('handleResolve error', error)
      showErrorToast({ message: 'Something went wrong' })
    }
    unpinFlagAfterAnimation(flag)
  }

  return (
    <ControlArea.Root onClose={onClose} className={className}>
      <div className="flag-control-area__scroll-body dome-gap18">
        <div style={{ padding: '0px 18px' }} className="dome-d-flex dome-flex-column dome-gap18">
          <ControlArea.Header title={title} subtitle={subtitle} />
          {processorWorking && (
            <ControlArea.Form editingFlag={editingFlag} onSave={handleSave} onCancelEdit={() => setEditingFlag(null)} />
          )}
          <ControlArea.TabBar tabs={PROCESSOR_TABS} activeTab={activeTab} onChange={setActiveTab} />
        </div>
        <ul
          style={{
            padding: '0px 18px',
            margin: 0,
            listStyle: 'none',
            overflow: 'auto',
            flex: 1,
            minHeight: 0,
          }}
        >
          {flagsDisplayed.map((flag) => {
            const animating = animatingFlags.get(flag.id)
            const displayFlag = animating ? animating.flag : flag
            const isResolved = animating
              ? !PROCESSOR_RESOLVED_STATES.includes(animating.flag.state)
              : PROCESSOR_RESOLVED_STATES.includes(flag.state)
            const isReadOnly = isFlagReadonly({ role, processorWorking, flag, currentRoundNumber })

            const flagFileName =
              flag.flaggable_type === 'PackageDocument' ? docLabelById[flag.flaggable_id] ?? fileName : fileName

            return (
              <li key={flag.id}>
                <ProcessorFlagItem
                  gap={12}
                  flag={displayFlag}
                  fileName={flagFileName}
                  isDraft={flag.state === FLAG_STATE.DRAFTED && processorWorking}
                  readOnly={isReadOnly}
                  resolved={isResolved}
                  isResolving={!!animating}
                  onEdit={setEditingFlag}
                  onDelete={handleDelete}
                  onResolve={(value) => handleResolve(flag, value)}
                />
              </li>
            )
          })}
        </ul>
        {flagsDisplayed.length === 0 && (
          <div style={{ padding: '0px 18px' }}>
            <EmptyState />
          </div>
        )}
      </div>
      <AppToastContainer />
    </ControlArea.Root>
  )
}

// ─── Entry Point ─────────────────────────────────────────────────────────────

/**
 * @param {{
 *   target: { flaggableType: string, flaggableId: number, packageId: string },
 *   currentRoundNumber: number,
 *   role: 'managing_agent' | 'closing_agent' | 'alterations_admin' | 'broker' | 'resident' | 'client' | 'board_member'
 *   title?: string,
 *   subtitle?: string,
 *   fileName?: string,
 *   onClose?: () => void,
 *   processorWorking?: boolean,
 * }} props
 */
const FlagControlArea = (props) => {
  const { role } = props
  if (APPLICANT_ROLES.includes(role)) return <ApplicantControlArea {...props} />
  if (PROCESSOR_ROLES.includes(role)) return <ProcessorControlArea {...props} />
}

export default FlagControlArea
